import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent =
      /** @type {TextComponent | undefined} */
      (lineItemRow.querySelector('text-component'));

    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,

      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter(
        (row) =>
          row.dataset.parentKey === cartItemRowToRemove.dataset.key
      ),
    ];

    // If the cart item row is the last row,
    // optimistically trigger the cart empty state
    const isEmptyCart =
      rowsToRemove.length == this.refs.cartItemRows.length;

    const template =
      document.getElementById('empty-cart-template');

    if (
      isEmptyCart &&
      template instanceof HTMLTemplateElement
    ) {
      const clone =
        document.importNode(template.content, true);

      startViewTransition(
        () => {
          this.replaceChildren(clone);
        },
        [
          this.isDrawer
            ? 'empty-cart-drawer'
            : 'empty-cart-page',
        ]
      );

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) {
        return remove();
      }

      row.style.setProperty(
        '--row-height',
        `${row.clientHeight}px`
      );

      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   *
   * @param {Object} config
   * @param {number} config.line
   * @param {number} config.quantity
   * @param {string} config.action
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker =
      cartPerformance.createStartingMarker(
        `${config.action}:user-action`
      );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents =
      document.querySelectorAll(
        'cart-items-component'
      );

    const sectionsToUpdate =
      new Set([this.sectionId]);

    cartItemsComponents.forEach((item) => {
      if (
        item instanceof HTMLElement &&
        item.dataset.sectionId
      ) {
        sectionsToUpdate.add(
          item.dataset.sectionId
        );
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections:
        Array.from(sectionsToUpdate).join(','),
      sections_url:
        window.location.pathname,
    });

    cartTotal?.shimmer();

    /*
     * ==========================================
     * PLUMPT / APPSTLE FIX
     * ==========================================
     *
     * Normal quantity changes still use the
     * theme's original cart/change.js request.
     *
     * But if the quantity becomes ZERO,
     * we first inspect the cart and remove
     * the subscription AND any Appstle gift
     * linked to that subscription.
     *
     * This prevents the $0 Welcome Kit from
     * remaining in the cart by itself.
     */

    const cartRequest =
      quantity === 0

        ? this.#removeLineAndLinkedAppstleGifts(
            line,
            sectionsToUpdate
          )

        : fetch(
            `${Theme.routes.cart_change_url}`,
            fetchConfig('json', { body })
          );

    cartRequest

      .then((response) => {
        return response.text();
      })

      .then((responseText) => {
        const parsedResponseText =
          JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(
            line,
            parsedResponseText
          );

          return;
        }

        const newSectionHTML =
          new DOMParser().parseFromString(
            parsedResponseText.sections[
              this.sectionId
            ],
            'text/html'
          );

        // Grab the new cart item count
        // from a hidden element
        const newCartHiddenItemCount =
          newSectionHTML.querySelector(
            '[ref="cartItemCount"]'
          )?.textContent;

        const newCartItemCount =
          newCartHiddenItemCount
            ? parseInt(
                newCartHiddenItemCount,
                10
              )
            : 0;

        // Update data-cart-quantity
        // for all matching variants
        this.#updateQuantitySelectors(
          parsedResponseText
        );

        this.dispatchEvent(
          new CartUpdateEvent(
            parsedResponseText,
            this.sectionId,
            {
              itemCount:
                newCartItemCount,

              source:
                'cart-items-component',

              sections:
                parsedResponseText.sections,
            }
          )
        );

        morphSection(
          this.sectionId,

          parsedResponseText.sections[
            this.sectionId
          ],

          {
            mode: this.isDrawer
              ? 'hydration'
              : 'full',
          }
        );

        this.#updateCartQuantitySelectorButtonStates();
      })

      .catch((error) => {
        console.error(error);
      })

      .finally(() => {
        this.#enableCartItems();

        cartPerformance.measureFromMarker(
          cartPerformaceUpdateMarker
        );
      });
  }

  /**
   * ==========================================
   * PLUMPT / APPSTLE LINKED GIFT REMOVAL
   * ==========================================
   *
   * Removes the selected Shopify cart line
   * AND any Appstle free gift connected to it.
   *
   * Appstle stores the relationship using:
   *
   * _appstle_parent_product
   * _appstle_parent_selling_plan
   *
   * @param {number} line
   * @param {Set<string>} sectionsToUpdate
   * @returns {Promise<Response>}
   */
  async #removeLineAndLinkedAppstleGifts(
    line,
    sectionsToUpdate
  ) {
    const shopifyRoot =
      window.Shopify?.routes?.root || '/';

    try {

      /*
       * ========================================
       * STEP 1
       *
       * READ THE CURRENT CART
       * ========================================
       */

      const cartResponse =
        await fetch(
          `${shopifyRoot}cart.js`,
          {
            method: 'GET',

            headers: {
              Accept:
                'application/json',
            },

            cache: 'no-store',
          }
        );

      if (!cartResponse.ok) {
        throw new Error(
          `Unable to read cart (${cartResponse.status})`
        );
      }

      const cart =
        await cartResponse.json();

      /*
       * Shopify line numbers are 1-based.
       * JavaScript array positions are 0-based.
       */

      const targetItem =
        cart.items?.[line - 1];

      if (!targetItem?.key) {
        throw new Error(
          'Cart line could not be found'
        );
      }

      /*
       * ========================================
       * STEP 2
       *
       * ALWAYS REMOVE THE ITEM THE CUSTOMER
       * ACTUALLY CLICKED
       * ========================================
       */

      const updates = {
        [targetItem.key]: 0,
      };

      /*
       * ========================================
       * STEP 3
       *
       * FIND THE SUBSCRIPTION SELLING PLAN
       * ========================================
       */

      const sellingPlanId =
        targetItem
          .selling_plan_allocation
          ?.selling_plan
          ?.id;

      /*
       * Appstle may use one of Shopify's
       * product identifiers in its parent
       * property.
       *
       * Therefore we compare against:
       *
       * - item ID
       * - variant ID
       * - product ID
       */

      const possibleParentIds =
        new Set(
          [
            targetItem.id,
            targetItem.variant_id,
            targetItem.product_id,
          ]

            .filter(
              (value) =>
                value !== undefined &&
                value !== null
            )

            .map(
              (value) =>
                String(value)
            )
        );

      /*
       * ========================================
       * STEP 4
       *
       * IF THIS IS A SUBSCRIPTION,
       * FIND EVERY APPSTLE GIFT
       * BELONGING TO IT.
       * ========================================
       */

      if (
        sellingPlanId !== undefined &&
        sellingPlanId !== null
      ) {

        for (
          const item of cart.items || []
        ) {

          if (
            !item?.key ||
            item.key === targetItem.key
          ) {
            continue;
          }

          const properties =
            item.properties || {};

          const appstleParentProduct =
            properties[
              '_appstle_parent_product'
            ];

          const appstleParentSellingPlan =
            properties[
              '_appstle_parent_selling_plan'
            ];

          /*
           * This gift belongs to the
           * subscription being deleted IF:
           *
           * 1. Its Appstle parent product
           *    matches the subscription
           *
           * AND
           *
           * 2. Its Appstle selling plan
           *    matches the subscription
           */

          const belongsToRemovedParent =

            appstleParentProduct !==
              undefined &&

            appstleParentProduct !==
              null &&

            possibleParentIds.has(
              String(
                appstleParentProduct
              )
            ) &&

            String(
              appstleParentSellingPlan ?? ''
            ) ===
              String(sellingPlanId);

          /*
           * If it belongs to the subscription,
           * set that gift's quantity to ZERO too.
           */

          if (
            belongsToRemovedParent
          ) {
            updates[item.key] = 0;
          }
        }
      }

      /*
       * ========================================
       * STEP 5
       *
       * SEND ONE CART UPDATE THAT REMOVES
       * EVERYTHING TOGETHER
       * ========================================
       */

      const updateBody =
        JSON.stringify({
          updates,

          sections:
            Array.from(
              sectionsToUpdate
            ).join(','),

          sections_url:
            window.location.pathname,
        });

      /*
       * Use Shopify's cart update route.
       */

      const cartUpdateUrl =
        Theme.routes.cart_update_url ||
        `${shopifyRoot}cart/update.js`;

      const separator =
        cartUpdateUrl.includes('?')
          ? '&'
          : '?';

      /*
       * Appstle uses appstle=true on its
       * cart reconciliation requests.
       *
       * We are removing the parent and its
       * linked gift together, so send this
       * as an Appstle-aware update.
       */

      return fetch(
        `${cartUpdateUrl}${separator}appstle=true`,

        fetchConfig(
          'json',
          {
            body: updateBody,
          }
        )
      );

    } catch (error) {

      console.error(
        '[Plumpt/Appstle] Linked gift removal failed:',
        error
      );

      /*
       * IMPORTANT SAFETY BEHAVIOR:
       *
       * If we cannot determine which gift
       * belongs to the subscription,
       * DON'T continue by removing only
       * the $60 patches.
       *
       * Otherwise the customer could be
       * left with the free $0 gift.
       */

      throw error;
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   *
   * @param {number} line
   * @param {Object} parsedResponseText
   * @param {string} parsedResponseText.errors
   */
  #handleCartError = (
    line,
    parsedResponseText
  ) => {

    const quantitySelector =
      this.refs.quantitySelectors[
        line - 1
      ];

    const quantityInput =
      quantitySelector?.querySelector(
        'input'
      );

    if (!quantityInput) {
      throw new Error(
        'Quantity input not found'
      );
    }

    quantityInput.value =
      quantityInput.defaultValue;

    const cartItemError =
      this.refs[
        `cartItemError-${line}`
      ];

    const cartItemErrorContainer =
      this.refs[
        `cartItemErrorContainer-${line}`
      ];

    if (
      !(
        cartItemError instanceof
        HTMLElement
      )
    ) {
      throw new Error(
        'Cart item error not found'
      );
    }

    if (
      !(
        cartItemErrorContainer instanceof
        HTMLElement
      )
    ) {
      throw new Error(
        'Cart item error container not found'
      );
    }

    cartItemError.textContent =
      parsedResponseText.errors;

    cartItemErrorContainer
      .classList
      .remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {
   *   DiscountUpdateEvent |
   *   CartUpdateEvent |
   *   CartAddEvent
   * } event
   */
  #handleCartUpdate = (event) => {

    if (
      event instanceof
      DiscountUpdateEvent
    ) {

      sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
        }
      );

      return;
    }

    if (event.target === this) {
      return;
    }

    const cartItemsHtml =
      event.detail.data.sections?.[
        this.sectionId
      ];

    if (cartItemsHtml) {

      morphSection(
        this.sectionId,
        cartItemsHtml
      );

      // Update button states
      // after section morph
      this.#updateCartQuantitySelectorButtonStates();

    } else {

      sectionRenderer.renderSection(
        this.sectionId,
        {
          cache: false,
        }
      );

    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add(
      'cart-items-disabled'
    );
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove(
      'cart-items-disabled'
    );
  }

  /**
   * Updates quantity selectors for
   * all matching variants in the cart.
   *
   * @param {Object} updatedCart
   * @param {
   *   Array<{
   *     variant_id: number,
   *     quantity: number
   *   }>
   * } [updatedCart.items]
   */
  #updateQuantitySelectors(
    updatedCart
  ) {

    if (!updatedCart.items) {
      return;
    }

    for (
      const item of updatedCart.items
    ) {

      const variantId =
        item.variant_id.toString();

      const selectors =
        document.querySelectorAll(
          `quantity-selector-component[data-variant-id="${variantId}"]`
        );

      for (
        const selector of selectors
      ) {

        const input =
          selector.querySelector(
            'input[data-cart-quantity]'
          );

        if (!input) {
          continue;
        }

        input.setAttribute(
          'data-cart-quantity',
          item.quantity.toString()
        );

        // Update quantity selector's
        // internal state
        if (
          'updateCartQuantity'
            in selector &&

          typeof
            selector
              .updateCartQuantity ===
            'function'
        ) {

          selector
            .updateCartQuantity();

        }
      }
    }
  }

  /**
   * Updates button states for all
   * cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {

    for (
      const selector of
      document.querySelectorAll(
        'cart-quantity-selector-component'
      )
    ) {

      /** @type {any} */
      (selector)
        .updateButtonStates?.();

    }
  }

  /**
   * Gets the section id.
   *
   * @returns {string}
   */
  get sectionId() {

    const { sectionId } =
      this.dataset;

    if (!sectionId) {
      throw new Error(
        'Section id missing'
      );
    }

    return sectionId;
  }

  /**
   * @returns {boolean}
   * Whether the component is a drawer.
   */
  get isDrawer() {
    return (
      this.dataset.drawer !==
      undefined
    );
  }
}

if (
  !customElements.get(
    'cart-items-component'
  )
) {

  customElements.define(
    'cart-items-component',
    CartItemsComponent
  );

}
/* ==========================================
   PLUMPT / APPSTLE CART DISPLAY ORDER

   Keeps the paid subscription first
   and moves its linked Appstle gift
   directly underneath it.

   IMPORTANT:
   This changes DISPLAY ORDER ONLY.
   It does not change cart quantities,
   Appstle linking, pricing, or removal logic.
========================================== */

(() => {

  let reorderTimer = null;
  let reorderRunning = false;


  /**
   * Read the current Shopify cart.
   */
  async function getPlumptCart() {

    const root =
      window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${root}cart.js`,
      {
        method: 'GET',

        headers: {
          Accept: 'application/json'
        },

        cache: 'no-store'
      }
    );


    if (!response.ok) {

      throw new Error(
        'Unable to read cart for gift ordering.'
      );

    }


    return response.json();

  }


  /**
   * Move every Appstle-linked gift
   * directly underneath its parent product.
   */
  async function reorderAppstleGifts() {

    if (reorderRunning) {
      return;
    }


    const cartComponents =
      document.querySelectorAll(
        'cart-items-component'
      );


    if (!cartComponents.length) {
      return;
    }


    reorderRunning = true;


    try {

      const cart =
        await getPlumptCart();


      if (
        !cart.items ||
        !cart.items.length
      ) {
        return;
      }


      /*
       * Find products containing Appstle's
       * private parent relationship properties.
       */
      const linkedGifts =
        cart.items.filter(
          function (item) {

            const properties =
              item.properties || {};


            return (
              properties._appstle_parent_product !==
                undefined &&

              properties._appstle_parent_product !==
                null &&

              properties._appstle_parent_selling_plan !==
                undefined &&

              properties._appstle_parent_selling_plan !==
                null
            );

          }
        );


      if (!linkedGifts.length) {
        return;
      }


      /*
       * Work on every cart-items-component.
       *
       * This means it works in:
       *
       * - Side cart drawer
       * - Full cart page
       */
      cartComponents.forEach(
        function (component) {

          const tbody =
            component.querySelector(
              '.cart-items__table tbody'
            );


          if (!tbody) {
            return;
          }


          /*
           * Horizon exposes each Shopify
           * cart line key using data-key.
           */
          const rows =
            Array.from(
              tbody.querySelectorAll(
                '.cart-items__table-row[data-key]'
              )
            );


          const rowByKey =
            new Map();


          rows.forEach(
            function (row) {

              if (row.dataset.key) {

                rowByKey.set(
                  row.dataset.key,
                  row
                );

              }

            }
          );


          /*
           * Group gifts by the parent line
           * they belong to.
           */
          const giftsByParent =
            new Map();


          linkedGifts.forEach(
            function (gift) {

              const properties =
                gift.properties || {};


              const appstleParentProduct =
                String(
                  properties
                    ._appstle_parent_product
                );


              const appstleParentSellingPlan =
                String(
                  properties
                    ._appstle_parent_selling_plan
                );


              /*
               * Find the actual subscription
               * line Appstle says owns this gift.
               */
              const parent =
                cart.items.find(
                  function (item) {

                    /*
                     * Compare all useful Shopify
                     * product identifiers.
                     */
                    const possibleParentIds =
                      [
                        item.id,
                        item.variant_id,
                        item.product_id
                      ]

                      .filter(
                        function (value) {

                          return (
                            value !== undefined &&
                            value !== null
                          );

                        }
                      )

                      .map(
                        function (value) {

                          return String(value);

                        }
                      );


                    const sellingPlanId =
                      item
                        .selling_plan_allocation
                        ?.selling_plan
                        ?.id;


                    return (
                      possibleParentIds.includes(
                        appstleParentProduct
                      ) &&

                      String(
                        sellingPlanId ?? ''
                      ) ===
                        appstleParentSellingPlan
                    );

                  }
                );


              if (
                !parent ||
                !parent.key ||
                !gift.key
              ) {
                return;
              }


              if (
                !giftsByParent.has(
                  parent.key
                )
              ) {

                giftsByParent.set(
                  parent.key,
                  []
                );

              }


              giftsByParent
                .get(parent.key)
                .push(gift);

            }
          );


          /*
           * Put each gift immediately AFTER
           * its paid parent subscription.
           */
          giftsByParent.forEach(
            function (
              gifts,
              parentKey
            ) {

              const parentRow =
                rowByKey.get(
                  parentKey
                );


              if (!parentRow) {
                return;
              }


              /*
               * Start with the paid product.
               */
              let insertAfter =
                parentRow;


              gifts.forEach(
                function (gift) {

                  const giftRow =
                    rowByKey.get(
                      gift.key
                    );


                  if (!giftRow) {
                    return;
                  }


                  /*
                   * Only move it if it isn't
                   * already directly underneath.
                   */
                  if (
                    insertAfter
                      .nextElementSibling !==
                    giftRow
                  ) {

                    insertAfter
                      .insertAdjacentElement(
                        'afterend',
                        giftRow
                      );

                  }


                  /*
                   * If there are multiple gifts,
                   * the next one goes underneath
                   * the previous gift.
                   */
                  insertAfter =
                    giftRow;

                }
              );

            }
          );

        }
      );


    } catch (error) {

      console.warn(
        '[Plumpt/Appstle] Could not reorder cart gifts:',
        error
      );


    } finally {

      reorderRunning = false;

    }

  }


  /**
   * Debounce so Shopify/Appstle can finish
   * updating the cart before we reorder it.
   */
  function scheduleGiftReorder() {

    if (reorderTimer) {

      clearTimeout(
        reorderTimer
      );

    }


    reorderTimer =
      setTimeout(
        function () {

          reorderTimer = null;

          reorderAppstleGifts();

        },
        100
      );

  }


  /**
   * Watch each Horizon cart component.
   *
   * Whenever Horizon refreshes its rows,
   * put the gift back underneath its parent.
   */
  function observeCartComponents() {

    document
      .querySelectorAll(
        'cart-items-component'
      )
      .forEach(
        function (component) {

          if (
            component.dataset
              .plumptGiftOrderObserver ===
            'true'
          ) {
            return;
          }


          component.dataset
            .plumptGiftOrderObserver =
            'true';


          const observer =
            new MutationObserver(
              function () {

                scheduleGiftReorder();

              }
            );


          observer.observe(
            component,
            {
              childList: true,
              subtree: true
            }
          );

        }
      );

  }


  /**
   * Run whenever Horizon announces
   * that the cart changed.
   */
  document.addEventListener(
    'cart:update',
    function () {

      observeCartComponents();

      scheduleGiftReorder();

    }
  );


  /**
   * Initial setup.
   */
  function initializePlumptGiftOrdering() {

    observeCartComponents();

    scheduleGiftReorder();

  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      initializePlumptGiftOrdering
    );

  } else {

    initializePlumptGiftOrdering();

  }

})();